import { useState } from "react";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import RenderWithTooltip, {
  CustomTrimmedTooltip,
} from "../../../../components/RenderWithTooltip";
import Text from "../../../../components/Text";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useQueryParams } from "../../../../hooks/use-query-param";
import {
  MEDICATION_DOSAGE_LIST_MAPPER,
  MEDICATION_ROUTE_LIST_MAPPER,
} from "../../../../pages/app/provider/patient/patient-list/patient-chart-new/clinical-details/components/ClinicalLayout-constant";
import { Medication } from "../../../../pages/app/provider/patient/patient-list/patient-chart-new/medication/medication-constant";
import { usePatientPortalMedicationListHealthRecord } from "../../../../queries/medication/usePatientPortalMedicationListHealthRecord";
import { formatDate } from "../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../utils/utils";

const PatientPortalMedicalHistory = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [currentUser] = useCurrentUser();
  const showPastMedications = useQueryParams("showPastMedications") === "true";

  const { data: medicationList } = usePatientPortalMedicationListHealthRecord({
    patientUuid: currentUser?.uuid ?? "",
    pagination,
    archive: false,
    active: showPastMedications ? false : true,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const columns = [
    {
      field: "medication_name",
      headerName: "Medication Name",
      flex: 1,
      renderCell: ({ value }: any) =>
        value ? <CustomTrimmedTooltip value={getValueFromObj(value)} /> : "-",
    },
    {
      field: "sig_details",
      headerName: "Sig Details",
      flex: 1,
      renderCell: MedicationsDirection,
    },
    {
      field: "prescribedBy",
      headerName: "Prescriber",
      flex: 1,
      renderCell: ({ value }: any) =>
        Object?.keys(value ?? {})?.length > 0
          ? (Object.values(value)?.[0] as any)
          : "-",
    },
    {
      field: "medication_start_date",
      headerName: "Start Date",
      flex: 1,
      renderCell: ({ row }: any) =>
        row?.medicationStartDate ? formatDate(row?.medicationStartDate) : "-",
    },
    {
      field: "medication_last_date",
      headerName: "End Date",
      flex: 1,
      renderCell: ({ row }: any) =>
        row?.lifeTime
          ? "Lifetime"
          : row?.medicationLastDate
            ? formatDate(row?.medicationLastDate)
            : "-",
    },
    {
      field: "remaining_days",
      headerName: "Remaining Days",
      flex: 1,
      renderCell: ({ value }: any) =>
        value?.remainingDays ? value?.remainingDays : "-",
    },
    {
      field: "refill_request",
      headerName: "Refill Request",
      flex: 1,
      renderCell: ({ value }: any) =>
        value?.refillRequest ? (
          <Text isTabletext>-</Text>
        ) : (
          <Text
            isTabletext
            color="primary.main"
            sx={{ cursor: "pointer", color: "#104E84", fontWeight: 500 }}
            onClick={() => {
              // TODO: Implement functionality
            }}
          >
            Request Refill
          </Text>
        ),
    },
  ];

  return (
    <CustomDataGridV2
      getRowId={(row) => row?.uuid}
      rows={medicationList?.data?.content ?? []}
      rowCount={medicationList?.data?.page?.totalElements ?? 0}
      columns={columns}
      paginationModel={pagination}
      onPaginationModelChange={setPagination}
    />
  );
};

export default PatientPortalMedicalHistory;

type MedicationsDirectionProps = {
  row: Medication;
};

const MedicationsDirection = ({ row }: MedicationsDirectionProps) => {
  const calcDays = () => {
    if (!row?.medicationStartDate || !row?.medicationLastDate) return 0;
    const startDate = new Date(row?.medicationStartDate);
    const endDate = new Date(row?.medicationLastDate);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const medicationData = `${row?.quantity ? "Take " + row?.quantity : ""} ${
    MEDICATION_DOSAGE_LIST_MAPPER[row?.dosage ?? ""] ?? ""
  } ${
    row?.dosageWhen
      ? replaceUnderScoreAddSpace(row?.dosageWhen?.toLocaleLowerCase())
      : ""
  } ${
    row?.direction
      ? "by " + MEDICATION_ROUTE_LIST_MAPPER[row?.direction] + " Route"
      : ""
  } ${
    row?.frequency?.toLocaleLowerCase()
      ? replaceUnderScoreAddSpace(row?.frequency?.toLocaleLowerCase())
      : ""
  } For ${
    row?.lifeTime ? "lifetime" : calcDays() + " days"
  }, starting from ${formatDate(row?.medicationStartDate)}`;

  return (
    <RenderWithTooltip value={medicationData?.trim() ? medicationData : "-"} />
  );
};
